/*
 * Copyright (c) 2024 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import React, { useMemo, useState } from 'react';
import {InputLabel, Typography} from "@mui/material";
import ArgosSelect from "../../../../../../components/Form/Select/ArgosSelect";
import ArgosMenuItem from "../../../../../../components/Form/Select/ArgosMenuItem";
import ArgosFlexbox from "../../../../../../components/Containers/ArgosFlexbox";
import useFetchUserData from "../../../../../../../hooks/useFetchUserData";
import ArgosButton, { ArgosLoadingButton } from "../../../../../../components/Buttons/ArgosButton";
import { usePostRequest } from "../../../../../../../api/helper";
import {updateOwnerEndpoint} from "../../../../constants";
import useGetUrlParams from "../../../../../../../hooks/useGetUrlParams";
import {SINGLE_DATASET_TREE_TYPE} from "../../../constants";
import {toast} from "react-hot-toast";
import {useAppSelector} from "../../../../../../../redux/hooks";
import {selectCurrentUser} from "../../../../../../../redux/ducks/AccessControl/slice";
import {useGetIsUserAdmin} from "../../../../../../../redux/ducks/AccessControl/accessControlHelpers";

interface IUpdateOwnerDialogProps {
  nodes: any;
  onClose: any
}

const UpdateOwnerDialog = (props: IUpdateOwnerDialogProps) => {
  const { nodes } = props;

  const {studyId} = useGetUrlParams()

  const [selectedOwner, setSelectedOwner] = useState("");
  const [isUpdatingOwner, setIsUpdatingOwner] = useState(false);

  const updateOwnerRequestHandle = usePostRequest(updateOwnerEndpoint);

  const gmid = useAppSelector(state => selectCurrentUser(state)?.id)
  const isAdmin = useGetIsUserAdmin();

  const datasetNodesUserOwns = useMemo(() => {
    return nodes.filter((node: any) => {
      return node.nodeType === 'dataset' && (node.owner === gmid || isAdmin)
    });
  }, [nodes, gmid, isAdmin]);

  const datasetIdsUserOwns = useMemo(() => {
    return new Set(datasetNodesUserOwns.map((node: any) => node.datasetId));
  }, [datasetNodesUserOwns, gmid]);

  const isUpdateOwnerDisabled = useMemo(() => {
    return !datasetIdsUserOwns.size || !selectedOwner
  }, [datasetIdsUserOwns, selectedOwner]);

  const { userData, isLoading } = useFetchUserData();

  const handleOwnerChange = (event: any) => {
    setSelectedOwner(event.target.value as string);
  };

  const handleUpdateOwner = async () => {
    setIsUpdatingOwner(true);
    try {
      await updateOwnerRequestHandle({
        datasetIds: Array.from(datasetIdsUserOwns),
        newOwner: selectedOwner,
        studyId,
        treeType: SINGLE_DATASET_TREE_TYPE
      });
      props.onClose()
    } catch (error) {
      console.error("Error updating owner:", error);
      toast.error("Error updating owner");
    } finally {
      setIsUpdatingOwner(false);
    }
  };

  const datasetNodesUserDoesNotOwn = useMemo(() => {
    return nodes.filter((node: any) => {
      return node.nodeType === 'dataset' && node.owner !== gmid && !isAdmin
    });
  }, [nodes, gmid, isAdmin]);

  const msg = useMemo(() => {
    if (!datasetNodesUserDoesNotOwn) return;
    const data = datasetNodesUserDoesNotOwn;
    let msgJsx = [];
    for (let i = 0; i < data.length; i++) {
      let node = data[i];
      if (node.nodeType !== 'dataset') continue;
      let owner = node.owner;
      let nodeName = node.name;
      const fullName = userData?.[owner]?.fullName ?? `gmid: ${owner}`;
      if (owner) {
        msgJsx.push(<li key={i}>({fullName}) {nodeName}</li>);
      } else {
        msgJsx.push(<li key={i}>{nodeName}</li>);
      }
    }
    return msgJsx;
  }, [datasetNodesUserDoesNotOwn, userData]);

  return (
    <ArgosFlexbox vertical rowGap={20} style={{overflow: 'auto', height: '100%'}}>
      <ArgosFlexbox style={{ alignItems: "center" }}>
        <InputLabel id="owner-select-label">Owner:</InputLabel>
        <ArgosSelect
          labelId="owner-select-label"
          id="owner-select"
          style={{ flexGrow: 1 }}
          value={selectedOwner}
          onChange={handleOwnerChange}
          disabled={isLoading || (!userData || !Object.keys(userData).length) || !datasetIdsUserOwns.size}
        >
          {
            Object.keys(userData).map((id: string) => (
              <ArgosMenuItem
                key={id}
                value={id}
              >
                {userData[id].fullName}
              </ArgosMenuItem>
            ))
          }
        </ArgosSelect>
        <ArgosLoadingButton disabled={isUpdateOwnerDisabled} isLoading={isUpdatingOwner} onClick={handleUpdateOwner}>
          Update
        </ArgosLoadingButton>
      </ArgosFlexbox>

      <div>
        {datasetNodesUserOwns.length === 0 && (
          <Typography>
            You do not own any of the selected datasets, so you cannot change their owner.
          </Typography>
        )}
      </div>

      <div style={{ marginTop: 0, justifySelf: 'center', flex: 1, overflow: 'auto' }}>
        {(datasetNodesUserDoesNotOwn.length > 0 && datasetIdsUserOwns.size > 0) && (
          <>
            <Typography>
              You do not own the following datasets, so their owner won't be updated:
            </Typography>
            <ul style={{marginTop: 10}}>
              {msg}
            </ul>
          </>
        )}
      </div>

      <div style={{ marginTop: 0, justifyContent: 'center', display: 'flex' }}>
        <ArgosButton onClick={props.onClose}>Close</ArgosButton>
      </div>
    </ArgosFlexbox>
  );
}

export default UpdateOwnerDialog;
